import React, { useState, useEffect } from 'react';
import { 
  FaUserCircle, 
  FaEye, 
  FaEyeSlash, 
  FaCalendarCheck, 
  FaEnvelopeOpenText, 
  FaUserShield, 
  FaPlus,
  FaFileMedical,
  FaClipboardCheck
} from 'react-icons/fa';

const Patient360Timeline = ({ users = [], appointments = [], submissions = [] }) => {
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [revealedFields, setRevealedFields] = useState({});
  const [newNote, setNewNote] = useState('');
  const [notes, setNotes] = useState({}); // mapped by patient email

  // Allow all users to appear in the list for testing purposes
  const patients = users;

  // Sync selected patient on load or if list changes
  useEffect(() => {
    if (patients.length > 0 && (!selectedPatient || !patients.some(p => p.email === selectedPatient.email))) {
      setSelectedPatient(patients[0]);
    }
  }, [users]);

  // HIPAA auto-masking timer hook
  useEffect(() => {
    const revealedKeys = Object.keys(revealedFields).filter(key => revealedFields[key]);
    if (revealedKeys.length === 0) return;

    const timer = setTimeout(() => {
      setRevealedFields({});
    }, 15000); // Re-mask after 15 seconds of inactivity

    return () => clearTimeout(timer);
  }, [revealedFields]);

  const toggleFieldReveal = (field) => {
    setRevealedFields(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const getMaskedValue = (field, value) => {
    if (!value) return '—';
    if (revealedFields[field]) return value;
    
    // Mask representations
    if (field === 'phone') return '••• - ••• - ••••';
    if (field === 'dob') return '•••• / •• / ••';
    if (field === 'address') return '•••••••••••••••••••••';
    if (field === 'email') {
      const parts = value.split('@');
      return `•••••••@${parts[1] || 'domain.com'}`;
    }
    return '••••••••';
  };

  const addClinicalNote = () => {
    if (!newNote.trim() || !selectedPatient) return;
    const email = selectedPatient.email;
    const updatedNotes = {
      ...notes,
      [email]: [
        {
          id: Date.now(),
          text: newNote,
          date: new Date().toLocaleString(),
          author: 'Admin/Therapist'
        },
        ...(notes[email] || [])
      ]
    };
    setNotes(updatedNotes);
    setNewNote('');
  };

  // Filter list
  const filteredPatients = patients.filter(p => 
    (p.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (p.email || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Generate history milestones dynamically
  const getTimelineEvents = (patient) => {
    if (!patient) return [];
    
    const appts = appointments.filter(a => a.email === patient.email);
    const msgs = submissions.filter(s => s.email === patient.email);

    const events = [];
    
    // User approval milestone
    events.push({
      id: 'registration',
      title: 'Intake Approval Completed',
      desc: `Registered as ${patient.role || 'viewer'} status. Identity credentials audited.`,
      icon: <FaUserShield className="text-emerald-500" />,
      date: patient.createdAt ? new Date(patient.createdAt).toLocaleDateString() : '05/18/2026',
      badgeColor: 'bg-emerald-100 text-emerald-800'
    });

    // Appointment events
    appts.forEach(a => {
      events.push({
        id: `appt-${a.id}`,
        title: `Appointment ${a.status.toUpperCase()}`,
        desc: `Therapist session: ${a.service} with ${a.therapist || 'assigned clinician'}. Slot: ${a.time}`,
        icon: <FaCalendarCheck className="text-purple-500" />,
        date: a.date,
        badgeColor: a.status === 'confirmed' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
      });
    });

    // Contact messages
    msgs.forEach(m => {
      events.push({
        id: `msg-${m.id}`,
        title: 'Patient Inquiry Sent',
        desc: `Contact query: "${m.message || m.subject}"`,
        icon: <FaEnvelopeOpenText className="text-amber-500" />,
        date: m.date || '05/19/2026',
        badgeColor: 'bg-amber-100 text-amber-800'
      });
    });

    return events.sort((a, b) => new Date(b.date) - new Date(a.date));
  };

  return (
    <div className="flex flex-col lg:flex-row gap-6 h-[calc(100vh-8rem)]">
      {/* Left Patient Directory Pane */}
      <div className="w-full lg:w-80 glass-panel bg-white  rounded-xl border border-slate-200  flex flex-col p-4">
        <h3 className="font-bold text-slate-800  text-md mb-3">Patient Directory</h3>
        <div className="relative mb-4">
          <input
            type="text"
            placeholder="Search by name or email"
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="w-full bg-slate-50  border border-slate-200  rounded-lg py-2 pl-9 pr-4 text-xs focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-slate-800 "
          />
          <FaUserCircle className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400  text-xs" />
        </div>

        <div className="flex-1 overflow-y-auto space-y-2">
          {filteredPatients.length === 0 ? (
            <div className="text-center py-8 text-xs text-slate-400 ">No active patients found</div>
          ) : (
            filteredPatients.map(p => (
              <button
                key={p.email}
                onClick={() => { setSelectedPatient(p); setRevealedFields({}); }}
                className={`w-full flex items-center gap-3 p-3 rounded-lg border text-left transition-all ${
                  selectedPatient?.email === p.email
                    ? 'border-purple-500 bg-purple-50/30 '
                    : 'border-slate-300  hover:bg-slate-50/50 :bg-slate-900/50'
                }`}
              >
                {p.avatar || p.avatarUrl ? (
                  <img src={p.avatar || p.avatarUrl} alt="avatar" className="w-9 h-9 rounded-full object-cover border border-slate-200 " />
                ) : (
                  <FaUserCircle className="text-3xl text-slate-300 " />
                )}
                <div className="truncate flex-1">
                  <h4 className="font-bold text-xs text-slate-800 ">{p.name}</h4>
                  <span className="text-xxs text-slate-400 ">{p.email}</span>
                </div>
                <span className="text-xxs px-2 py-0.5 rounded-full bg-slate-100  text-slate-600  border border-slate-300  capitalize">{p.role}</span>
              </button>
            ))
          )}
        </div>
      </div>

      {/* Right Content Panel - Patient 360 */}
      {selectedPatient ? (
        <div className="flex-1 flex flex-col lg:flex-row gap-6 overflow-y-auto">
          {/* HIPAA Bio Column */}
          <div className="w-full lg:w-72 glass-panel bg-white  rounded-xl border border-slate-200  p-5 flex flex-col">
            <div className="flex flex-col items-center border-b border-slate-300  pb-5 mb-5">
              {selectedPatient.avatar || selectedPatient.avatarUrl ? (
                <img src={selectedPatient.avatar || selectedPatient.avatarUrl} alt="avatar" className="w-20 h-20 rounded-full object-cover border-2 border-purple-100  mb-3" />
              ) : (
                <FaUserCircle className="text-6xl text-slate-300  mb-3" />
              )}
              <h2 className="font-extrabold text-slate-800  text-md text-center">{selectedPatient.name}</h2>
              <span className="text-xxs bg-emerald-50  text-emerald-700  border border-emerald-200  font-bold uppercase tracking-wider px-2.5 py-0.5 rounded-full mt-2">Active Patient</span>
            </div>

            {/* Masked Credentials Fields */}
            <div className="space-y-4 text-xs flex-1">
              <div>
                <span className="text-xxs text-slate-400  uppercase font-bold tracking-wider">Email Address</span>
                <div className="flex items-center justify-between mt-1">
                  <span className="font-semibold text-slate-700  select-all">{getMaskedValue('email', selectedPatient.email)}</span>
                  <button onClick={() => toggleFieldReveal('email')} className="text-slate-400  hover:text-purple-600 :text-purple-400 transition-colors">
                    {revealedFields.email ? <FaEyeSlash size={14} /> : <FaEye size={14} />}
                  </button>
                </div>
              </div>

              <div>
                <span className="text-xxs text-slate-400  uppercase font-bold tracking-wider">Phone Number</span>
                <div className="flex items-center justify-between mt-1">
                  <span className="font-semibold text-slate-700 ">{getMaskedValue('phone', selectedPatient.phone)}</span>
                  <button onClick={() => toggleFieldReveal('phone')} className="text-slate-400  hover:text-purple-600 :text-purple-400 transition-colors">
                    {revealedFields.phone ? <FaEyeSlash size={14} /> : <FaEye size={14} />}
                  </button>
                </div>
              </div>

              <div>
                <span className="text-xxs text-slate-400  uppercase font-bold tracking-wider">Date of Birth</span>
                <div className="flex items-center justify-between mt-1">
                  <span className="font-semibold text-slate-700 ">{getMaskedValue('dob', selectedPatient.dob)}</span>
                  <button onClick={() => toggleFieldReveal('dob')} className="text-slate-400  hover:text-purple-600 :text-purple-400 transition-colors">
                    {revealedFields.dob ? <FaEyeSlash size={14} /> : <FaEye size={14} />}
                  </button>
                </div>
              </div>

              <div>
                <span className="text-xxs text-slate-400  uppercase font-bold tracking-wider">Mailing Address</span>
                <div className="flex items-center justify-between mt-1">
                  <span className="font-semibold text-slate-700  truncate max-w-[200px]" title={selectedPatient.address}>{getMaskedValue('address', selectedPatient.address)}</span>
                  <button onClick={() => toggleFieldReveal('address')} className="text-slate-400  hover:text-purple-600 :text-purple-400 transition-colors">
                    {revealedFields.address ? <FaEyeSlash size={14} /> : <FaEye size={14} />}
                  </button>
                </div>
              </div>
            </div>

            <div className="text-xxs text-slate-400  border-t border-slate-300  pt-3 text-center">
              HIPAA Auto-mask active (15s idle timer)
            </div>
          </div>

          {/* Timeline & Notes Column */}
          <div className="flex-1 flex flex-col gap-6">
            {/* Timeline View */}
            <div className="flex-1 glass-panel bg-white  rounded-xl border border-slate-200  p-5 flex flex-col min-h-[220px]">
              <div className="flex items-center gap-2 border-b border-slate-300  pb-3 mb-4">
                <FaClipboardCheck className="text-purple-600  text-md" />
                <h3 className="font-bold text-slate-800  text-sm">Patient 360 Event History</h3>
              </div>

              <div className="flex-1 overflow-y-auto space-y-4 pr-2">
                {getTimelineEvents(selectedPatient).map(event => (
                  <div key={event.id} className="relative pl-6 pb-2 border-l border-slate-300  last:border-0 last:pb-0">
                    <span className="absolute -left-3 top-0 bg-white  p-1 rounded-full border border-slate-200 ">
                      {event.icon}
                    </span>
                    <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
                      <h4 className="font-bold text-xs text-slate-800 ">{event.title}</h4>
                      <span className="text-slate-400  text-xxs">{event.date}</span>
                    </div>
                    <p className="text-slate-600  text-xs">{event.desc}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Clinical Session Notes */}
            <div className="glass-panel bg-white  rounded-xl border border-slate-200  p-5">
              <div className="flex items-center gap-2 border-b border-slate-300  pb-3 mb-4">
                <FaFileMedical className="text-purple-600  text-md" />
                <h3 className="font-bold text-slate-800  text-sm">Clinical Session Notes</h3>
              </div>

              <div className="flex gap-3 mb-4">
                <input
                  type="text"
                  placeholder="Record summary notes of the session..."
                  value={newNote}
                  onChange={e => setNewNote(e.target.value)}
                  className="flex-1 bg-slate-50  border border-slate-200  rounded-lg px-3 py-2 text-xs focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-slate-800 "
                />
                <button
                  onClick={addClinicalNote}
                  className="bg-purple-600 hover:bg-purple-700 text-white px-3 py-2 rounded-lg text-xs font-bold flex items-center gap-1.5 transition-colors"
                >
                  <FaPlus size={11} /> Record
                </button>
              </div>

              <div className="space-y-2 max-h-36 overflow-y-auto">
                {!(notes[selectedPatient.email] || []).length ? (
                  <p className="text-xs text-slate-400  italic">No notes recorded yet for this patient.</p>
                ) : (
                  notes[selectedPatient.email].map(note => (
                    <div key={note.id} className="bg-slate-50/70  p-3 rounded-lg border border-slate-300  text-xs">
                      <div className="flex justify-between text-slate-400  text-xxs mb-1">
                        <span className="font-bold">{note.author}</span>
                        <span>{note.date}</span>
                      </div>
                      <p className="text-slate-700  font-medium">{note.text}</p>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="flex-1 glass-panel bg-white  rounded-xl border border-slate-200  grid place-items-center text-slate-400 ">
          Select a patient to view their comprehensive timeline
        </div>
      )}
    </div>
  );
};

export default Patient360Timeline;
